import React from 'react'
import ReactTooltip from 'react-tooltip'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { CloudX } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import ScoreBar from '../../Charts/HorizontalChart/ScoreBar'
import { getStars, getDiffColor, getPercentageDiffText } from '../helpers'
import UpsellingBlocker from '../UpsellingBlocker'
import Can from '../../Can'
import Icon from '../../Icons'

const ReputationManagement = ({ reputationManagementData, loading, datesDescriptions }) => {
  const { t } = useTranslation(['building-analytics'])

  const getStatistics = () => {
    const satisfactionScore =
      reputationManagementData &&
      reputationManagementData.reputation &&
      reputationManagementData.reputation.satisfactionScore

    const surveys = (reputationManagementData && reputationManagementData.surveys) || []

    return satisfactionScore && satisfactionScore.value ? (
      <div>
        {surveys.length ? (
          surveys.map(({ id, name, rating: { value } }) =>
            value ? (
              <div key={id} className="statistic-line-wrapper">
                <span>{name}</span>
                {getStars(value)}
                <Link className="redirect-button" to={`/dashboard/survey/${id}`}>
                  {t('building-analytics:common.view')}
                  <Icon
                    styles={{ width: '20px', paddingLeft: '5px' }}
                    viewBox="0 0 20 20"
                    icon={'arrowRight'}
                    svgProps={{
                      stroke: '#646464',
                      strokeLinecap: 'round',
                      strokeLinejoin: 'round',
                    }}
                    fill="none"
                  />
                </Link>
              </div>
            ) : null,
          )
        ) : (
          <p>{t('building-analytics:reputationManagement.noSurveysData')}</p>
        )}
        <div className="text-with-tooltip">
          <p className="score-title">
            {t('building-analytics:reputationManagement.satisfactionScore')}
          </p>
          <span data-tip data-for={`satisfaction-score-triggerTip`} style={{ display: 'flex' }}>
            <Icon
              icon="info"
              viewBox="0 0 25 25"
              styles={{
                width: '20px',
                height: '20px',
                marginLeft: '5px',
                border: '1px solid black',
                borderRadius: '50%',
              }}
            />
            <ReactTooltip
              className="tooltip"
              id={`satisfaction-score-triggerTip`}
              multiline
              place="right"
              effect="solid"
            >
              <React.Fragment>
                <div style={{ width: '200px' }}>
                  {t('building-analytics:reputationManagement.satisfactionScoreTooltip')}
                </div>
              </React.Fragment>
            </ReactTooltip>
          </span>
        </div>
        <div className="score-bar-with-text">
          <ScoreBar percentage={satisfactionScore.value.toFixed(2)} />
          {!['-', 'Infinity'].includes(satisfactionScore.percentageDiff) && (
            <span className={`percent-text ${getDiffColor(satisfactionScore)}`}>
              {getPercentageDiffText(satisfactionScore)}
            </span>
          )}
        </div>
      </div>
    ) : (
      <div className="no-data">
        <CloudX size={24} />
        <h3>{t('building-analytics:common.noDataTitle')}</h3>
        <p>{t('building-analytics:common.noDataDescription')}</p>
      </div>
    )
  }

  const getReviewsRequests = () => {
    const reviewsSent =
      reputationManagementData &&
      reputationManagementData.reputation &&
      reputationManagementData.reputation.reviewsSent

    const reviewsSentValue = (reviewsSent && reviewsSent.value) || 0
    return reviewsSent ? (
      <div className="no-data-reviews">
        <div className="no-data-reviews__top-wrapper">
          <h3 className="no-data-reviews__top-wrapper__number">{reviewsSentValue}</h3>
          {reviewsSent.percentageDiff !== '-' && (
            <div
              className={`no-data-reviews__top-wrapper__percent-wrapper  ${getDiffColor(
                reviewsSent,
              )}`}
            >
              <span className="percent-text">{getPercentageDiffText(reviewsSent)}</span>
            </div>
          )}
        </div>
        <p className="no-data-reviews__description">
          {t('building-analytics:reputationManagement.reviewsRequestSentDescription')}
        </p>
      </div>
    ) : (
      <div className="no-data-reviews">
        <div className="no-data-reviews__top-wrapper">
          <h3 className="no-data-reviews__top-wrapper__number">0</h3>
          <div className="no-data-reviews__top-wrapper__percent-wrapper">
            <span className="percent-text">00.00%</span>
          </div>
        </div>
        <p className="no-data-reviews__description">
          {t('building-analytics:reputationManagement.reviewsRequestSentDescription')}
        </p>
      </div>
    )
  }

  return (
    <div className="reputation-management">
      <div className="reputation-management__header">
        <div className="title-with-icon">
          <Icon styles={{ width: '24px', height: '24px' }} viewBox="0 0 24 24" icon="stars" />
          <h3>{t('building-analytics:reputationManagement.title')}</h3>
        </div>
        <p>
          {t('building-analytics:common.dateRangeCompared', {
            start: datesDescriptions[0],
            end: datesDescriptions[1],
          })}
        </p>
      </div>
      {loading ? (
        <div className="loading">
          <h3>....</h3>
          <h3>{t('building-analytics:common.loading')}</h3>
        </div>
      ) : (
        <Can
          on={'Navbar'}
          to={'rewards'}
          feedFlag={'rewardsAvailable'}
          upsellingLink={
            <UpsellingBlocker
              type="reputationManagement"
              text={t('building-analytics:reputationManagement.upsellingText')}
              wrapperClassName="reputation-management__upselling-blocker"
            />
          }
        >
          <div className="reputation-management__main-block">
            <div className="statistics-average-rating">
              <h3 className="title">
                {t('building-analytics:reputationManagement.averageRating')}
              </h3>
              {getStatistics()}
            </div>
            <div className="reviews-requests-sent">
              <h3 className="title">
                {t('building-analytics:reputationManagement.reviewsRequestsSent')}
              </h3>
              {getReviewsRequests()}
            </div>
          </div>
        </Can>
      )}
    </div>
  )
}

ReputationManagement.propTypes = {
  reputationManagementData: PropTypes.object,
  loading: PropTypes.bool,
  datesDescriptions: PropTypes.array,
}
export default ReputationManagement
